/**
 * Issue trackers through the platform (docs/plans/2026-10-project-person-machine-platform.md,
 * D215 round 5). For an organisation project the platform is the one writer to the tracker: it
 * keeps a shared board and a GitHub repository's issues (or an Azure Boards project's work items,
 * or a Jira Cloud project's issues) the same, once, always on, driven by the provider's webhooks and an hourly reconciliation, each
 * write audited with the person behind it. AIOE's own contract; the workbench copies it into
 * `shared/schemas/trackers.ts` (its own per-machine mirror stays for Personal-space and hub projects).
 *
 *   PUT  /projects/v1/:id/source                     owners: `trackers[]` beside the repositories (source.ts)
 *   GET  /projects/v1/:id/trackers                   members: each tracker with its standing (last sync, last webhook, linked tasks, errors)
 *   POST /projects/v1/:id/trackers/:tid/sync         owners: reconcile now → { pulled, pushed, linked, errors }
 *   GET  /projects/v1/:id/trackers/:tid/statuses     members: Jira only, the project's workflow statuses and where each lands
 *   GET  /projects/v1/:id/local-boards               members: the boards kept on a workbench, with any mirror each writes (C-26)
 *   POST /source/v1/webhooks/:connectionId/jira      Jira Cloud's webhook (issue and comment events), signed with the connection's secret
 *
 * A task carries its link in `metadata.tracker` ({@link taskTrackerLinkSchema}); a comment it made
 * at the tracker is remembered in `metadata.tracker.comments` by the board comment's id, so nothing
 * is written twice. Conflicts: the newer change wins by time, and the audit row says what was
 * overwritten. A change made at the tracker by a person is written to the board as that person
 * when their login is linked here (`/source/v1/identities`), else as the tracker.
 *
 * Assignment: a task's assignee who is a person here goes to the tracker as their linked account
 * (people.ts `linkedIdentitiesSchema`): the GitHub login, Azure Boards' `System.AssignedTo` as the
 * Azure DevOps uniqueName, Jira's assignee by accountId. A person who has not linked that provider
 * leaves the item unassigned, and the tracker's standing says so in its errors ("Jean has not
 * linked GitHub, so #12 is unassigned"). An assignee who is nobody here (a team member's name) is
 * left alone at the tracker.
 * One writer per target (C-26): a workbench that keeps a board locally may mirror it to the same
 * repository (or DevOps or Jira project) a platform tracker would write; two writers duplicate each
 * other's issues. `PUT /projects/v1/:id/source` therefore answers 409 {@link MirrorConflict} for a new or
 * re-targeted tracker whose provider and target a live workbench mirror writes, and a mirror reported
 * after the tracker adds an error to the tracker's standing and files one inbox record for the owners.
 * Words for people: tracker, issue, work item, linked, pulled, pushed.
 */
import { z } from "zod";
/** 2: `jira` joins the providers, with `jiraProject` and `issueType` on a tracker. */
export const TRACKERS_SCHEMA_VERSION = 2;
export const TRACKER_PROVIDERS = ["github-issues", "azure-boards", "jira"];
export const trackerProviderSchema = z.enum(TRACKER_PROVIDERS);
const keySchema = z.string().min(1).max(200).regex(/^[A-Za-z0-9_.:-]+$/, "letters, digits and _ . : -");
/** One tracker on a project: which issues (or work items) sync with which board. */
export const projectTrackerSchema = z.object({
    id: keySchema,
    provider: trackerProviderSchema,
    /** The source connection to go through; the project's source connection when left out. */
    connectionId: z.string().max(100).optional(),
    /** GitHub: `owner/name`. */
    repository: z.string().max(300).optional(),
    /** Azure Boards: the DevOps project; the source's when left out. */
    azureProject: z.string().max(200).optional(),
    /** Jira: the project key (`OPS`). Jira trackers go through a Jira connection named in `connectionId`. */
    jiraProject: z.string().regex(/^[A-Z][A-Z0-9_]{0,49}$/, "a Jira project key: capitals, digits and _").optional(),
    /** Jira: the issue type a board task becomes (Task when left out). */
    issueType: z.string().min(1).max(100).optional(),
    /** The board that mirrors it. */
    board: z.object({ rootId: keySchema, board: keySchema, /** The repository's name (AIOE 0.34.0): put on answers, never stored. */ rootName: z.string().max(200).optional() }),
    /** `two-way`: board changes go to the tracker too; `import`: the tracker is read only. */
    direction: z.enum(["two-way", "import"]).default("two-way"),
    filter: z
        .object({
        /** GitHub labels, or Azure Boards tags, an item must carry to be synced; any when empty. */
        labels: z.array(z.string().max(100)).max(20).optional(),
        /** Azure Boards work item types, or Jira issue types, to sync (User Story, Bug, Task); any when empty. */
        workItemTypes: z.array(z.string().max(100)).max(20).optional(),
    })
        .optional(),
    /** Tracker state → board status, over the defaults ({@link DEFAULT_STATUS_MAP}). */
    statusMap: z.record(z.string(), z.enum(["backlog", "todo", "in-progress", "review", "done", "blocked"])).optional(),
});
/** Where a tracker state lands on the board when the map says nothing. Keys are lower case. */
export const DEFAULT_STATUS_MAP = {
    "github-issues": { open: "todo", closed: "done" },
    "azure-boards": { new: "todo", "to do": "todo", proposed: "backlog", approved: "todo", committed: "todo", active: "in-progress", doing: "in-progress", "in progress": "in-progress", resolved: "review", closed: "done", done: "done", removed: "done" },
    jira: { backlog: "backlog", "to do": "todo", open: "todo", "selected for development": "todo", reopened: "todo", "in progress": "in-progress", "in development": "in-progress", "in review": "review", review: "review", "code review": "review", testing: "review", "in testing": "review", qa: "review", blocked: "blocked", "on hold": "blocked", done: "done", closed: "done", resolved: "done", "won't do": "done", cancelled: "done" },
};
/** What a board status becomes at the tracker. On Jira, a name the project's workflow lacks falls back to a status in the same category ({@link JIRA_BOARD_CATEGORY}). */
export const BOARD_TO_TRACKER = {
    "github-issues": { backlog: "open", todo: "open", "in-progress": "open", review: "open", blocked: "open", done: "closed" },
    "azure-boards": { backlog: "New", todo: "To Do", "in-progress": "Doing", review: "Resolved", blocked: "Doing", done: "Done" },
    jira: { backlog: "Backlog", todo: "To Do", "in-progress": "In Progress", review: "In Review", blocked: "Blocked", done: "Done" },
};
/** Jira's three status categories, which every workflow status belongs to. */
export const JIRA_STATUS_CATEGORIES = ["new", "indeterminate", "done"];
/** Where a Jira status the maps do not name lands, by its category. */
export const JIRA_CATEGORY_TO_BOARD = { new: "todo", indeterminate: "in-progress", done: "done" };
/** The category a board status needs at Jira when the project's workflow has no status of the mapped name. */
export const JIRA_BOARD_CATEGORY = { backlog: "new", todo: "new", "in-progress": "indeterminate", review: "indeterminate", blocked: "indeterminate", done: "done" };
/** The link a synced task carries at `metadata.tracker`. */
export const taskTrackerLinkSchema = z.object({
    trackerId: keySchema,
    provider: trackerProviderSchema,
    /** `owner/name#7` on GitHub, `#101` on Azure Boards, `OPS-7` on Jira. */
    id: z.string().max(300),
    number: z.number().int().positive(),
    url: z.string().max(1000),
    /** The tracker's own last change the platform has seen: a webhook at or before it is an echo. */
    remoteUpdatedAt: z.string().max(50).optional(),
    /** When the platform last wrote the task from the tracker, or the tracker from the task. */
    syncedAt: z.string().max(50),
    /** The task's version after the platform's last write: a higher version is a board change since. */
    syncedVersion: z.number().int().nonnegative().optional(),
    /** Board comment id → the tracker's comment id, for comments pushed; the tracker's comment ids pulled are its values too. */
    comments: z.record(z.string(), z.string()).optional(),
    /**
     * The board's assignee as the platform last pushed or pulled it ("" for nobody): a different one on
     * the board is a reassignment to push, as the person's linked account (people.ts `linked`). Absent
     * on links made before it was kept, which push only an assignment to a person who linked.
     */
    assignee: z.string().max(200).optional(),
});
/** The identity two writers must not share: GitHub `owner/repo` lower-cased, the Azure DevOps project (the part after any `organisation/`) lower-cased, the Jira key upper-cased. */
export function trackerTargetKey(provider, target) {
    const t = target.trim();
    if (provider === "github-issues")
        return t.toLowerCase();
    if (provider === "azure-boards")
        return t.slice(t.lastIndexOf("/") + 1).toLowerCase();
    return t.toUpperCase();
}
const PROVIDER_WORDS = { "github-issues": "GitHub", "azure-boards": "Azure Boards", jira: "Jira" };
export const trackerProviderWord = (p) => PROVIDER_WORDS[p];
//# sourceMappingURL=trackers.js.map